var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Float } from "./float.js";
import { Focus } from "./focus.js";
import { Tiles } from "./tiles.js";
/**
 * Dynamic optimal animated grid layout based on ratios
 */
export class Layout {
    constructor(options = {}) {
        this.queue = [];
        this.busy = false;
        this.pendingRemoval = new Map();
        this.options = Object.assign({ margin: 10, width: document.body.clientWidth, height: document.body.clientHeight, focus: {
                maximize: false,
            } }, options);
        this.tiles = new Tiles(this, this.options);
        this.floating = new Float(this, this.options);
        this.focuser = new Focus(this);
    }
    render(animate = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                this.enqueue(() => __awaiter(this, void 0, void 0, function* () {
                    const promises = [];
                    for (const removed of this.pendingRemoval.values()) {
                        if (!removed.state.completed) {
                            removed.elm.style.transitionDuration = ".0s";
                            removed.elm.style.top = `${removed.state.top}px`;
                            removed.elm.style.left = `${removed.state.left}px`;
                            removed.elm.style.width = `${removed.state.width}px`;
                            removed.elm.style.height = `${removed.state.height}px`;
                            removed.state.completed = true;
                        }
                        // give some time for transition to register initial position
                        yield new Promise((resolve) => setTimeout(resolve, 0));
                        removed.elm.style.transitionDuration = "300ms";
                        removed.elm.style.top = `${removed.transition.top}px`;
                        removed.elm.style.left = `${removed.transition.left}px`;
                        removed.elm.style.width = `${removed.transition.width}px`;
                        removed.elm.style.height = `${removed.transition.height}px`;
                        removed.elm.style.visibility = "hidden";
                        removed.transition.completed = true;
                        removed.state = removed.transition;
                    }
                    this.pendingRemoval.clear();
                    yield Promise.all([
                        ...promises,
                        this.tiles.render(animate),
                        this.floating.render(animate),
                        this.focuser.render(animate),
                    ]);
                    resolve();
                }));
            });
        });
    }
    enqueue(job) {
        return __awaiter(this, void 0, void 0, function* () {
            this.queue.push(job);
            if (!this.busy) {
                this.busy = true;
                try {
                    while (this.queue.length) {
                        yield Promise.race([
                            this.queue.shift()(),
                            new Promise((resolve) => setTimeout(resolve, 2000)),
                        ]);
                    }
                }
                catch (_a) {
                }
                finally {
                    this.busy = false;
                }
            }
        });
    }
    getRemovalPending(elm) {
        const box = this.pendingRemoval.get(elm);
        this.pendingRemoval.delete(elm);
        return box;
    }
    /**
     * position a new element in the tiles layout or update the ratio of one already in
     * @param div the box to control
     * @param ratio the ratio width/height to wish for
     */
    tile(div, ratio) {
        if (this.floating.contains(div)) {
            this.unfloat();
        }
        else if (this.focuser.contains(div)) {
            this.unfocus();
        }
        this.tiles.tile(div, ratio);
    }
    /**
     * remove element from layout but not from dom
     * @param div
     */
    untile(div) {
        this.tiles.untile(div);
    }
    /**
     * mainly resize parent container, but also update top, left offset and margin
     * @param options update stuff, top and left are for offsetting
     */
    update(options) {
        if (options.focus) {
            this.options.focus = Object.assign(Object.assign({}, this.options.focus), options.focus);
        }
        this.options = Object.assign(Object.assign({}, this.options), options);
        this.floating.update(this.options);
        this.focuser.update(this.options);
        if (!this.focuser.tiles) {
            this.tiles.update(this.options);
        }
    }
    /**
     * make a box larger than the rest
     */
    focus(div, ratio) {
        if (this.floating.contains(div)) {
            this.unfloat();
        }
        else if (this.tiles.contains(div)) {
            this.untile(div);
        }
        this.focuser.set(div, ratio);
    }
    /**
     * remove focus from a box
     */
    unfocus() {
        this.focuser.unset();
    }
    /**
     * set or reset a floating element
     * @param div
     * @param options
     */
    float(div, options = {}) {
        if (this.tiles.contains(div)) {
            this.untile(div);
        }
        else if (this.focuser.contains(div)) {
            this.unfocus();
        }
        this.floating.set(div, options);
    }
    /**
     * unset floating element
     */
    unfloat() {
        this.floating.unset();
    }
}
